// @flow
import React from 'react';
import PropTypes from 'prop-types';
import {Grid, Hidden, makeStyles, IconButton} from '@material-ui/core';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ArrowForward,
  ArrowBack,
} from '@material-ui/icons';

import template1 from '../../../../../assets/img/templates/TEMPLATE_1.webp';
import template2 from '../../../../../assets/img/templates/TEMPLATE_2.webp';
import template3 from '../../../../../assets/img/templates/TEMPLATE_3.webp';
import template4 from '../../../../../assets/img/templates/TEMPLATE_4.webp';

import {colors} from '../../../../../assets/styles/colors';
import {useState} from 'react';
import {useIsFeatureInTier} from '../../../../../utils/component';
import RestrictionModal from '../RestricionModal/RestrictionModal';
import {useStoreBusinessModel} from '../../../../store/utils';

const useStyles = makeStyles(theme => ({
  buttonSquare: {
    width: '100%',
    height: '100%',
    padding: 0,
    '& span': {
      height: '100%',
    },
    background: 'transparent',

    '&:hover': {
      background: 'transparent',
    },
  },

  templateItemContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    borderRadius: '8px',
  },
  skeletonSquare: {
    width: '100%',
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    borderRadius: '8px',
    padding: theme.spacing(4, 0, 2, 0),
  },
  containerCheckedBox: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  wrapSlider: {
    width: '100%',
  },
  slider: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    scrollSnapType: 'x mandatory',
  },
  arrowBack: {
    position: 'absolute',
    left: 0,
    zIndex: 1,
  },
  arrowForward: {
    position: 'absolute',
    right: 0,
    zIndex: 1,
  },
  itemSlider: {
    width: '100%',
    display: 'flex',
    flexShrink: '0',
    justifyContent: 'center',
    alignItems: 'center',
    transform0rigin: 'center center',
    transform: 'scale(1)',
    flexDirection: 'column',
    gap: '1rem',
    borderRadius: '8px',
  },
  containerDots: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dot: {
    width: '0.8rem',
    height: '0.8rem',
    borderRadius: '50%',
    backgroundColor: colors.grey300,
  },
  dotSelected: {
    backgroundColor: colors.grey500,
  },
  btnDot: {
    width: 'fit-content',
    '& .MuiButton-label': {
      width: 'fit-content',
    },
  },
  templateImage: {
    width: '180px',
    aspectRatio: '28/65',
    objectFit: 'cover',
  },
  goToStoreButton: {
    background: '#FFFFFF',
    color: colors.grey900,
    boxShadow: '0px 0.5px 20px 0px #60617014, 0px 0px 16px 0px #28293D14',
    paddingInline: '1rem',
    marginBottom: '1rem',
    '&:disabled': {
      pointerEvents: 'none',
      opacity: 0,
    },
  },
}));

type Props = {
  templateType?: string,
  setTemplateType: (id: string) => void,
};

const TEMPLATE_OPTIONS: {
  src: string,
  id: string,
  storeUrl?: string,
  background: string,
  businessModel?: string[],
}[] = [
  {
    src: template1,
    id: 'template-1',
    storeUrl: 'https://petsfun.tiendada.com/',
    background: '#ECF7FE',
    businessModel: ['PRODUCT'],
  },
  {
    src: template2,
    id: 'template-2',
    storeUrl: 'https://joliejewerly.tiendada.com/',
    background: '#F0EDE5',
    businessModel: ['PRODUCT'],
  },
  {
    src: template3,
    id: 'template-3',
    storeUrl: 'https://modnestinteriors.tiendada.com/',
    background: '#E7E8EA',
    businessModel: ['PRODUCT'],
  },
  {
    src: template4,
    id: 'template-4',
    background: '#DCE4E1',
    businessModel: ['SERVICE'],
  },
];

export default function TemplateSection(props: Props) {
  const {templateType, setTemplateType} = props;
  const classes = useStyles();

  const hasCustomTemplateFeature = useIsFeatureInTier('STOREFRONT_TEMPLATES');

  const businessModel = useStoreBusinessModel();

  const templatesToShow = TEMPLATE_OPTIONS.filter(to => {
    if (!to.businessModel) return true;
    return to.businessModel.includes(businessModel);
  });

  const [currentSlider, setCurrentSlider] = useState(0);
  const currentImage = templatesToShow[currentSlider];

  const handleViewSlider = (id: number) => {
    setCurrentSlider(id);
  };

  const handleNextSlider = () => {
    if (currentSlider < templatesToShow.length - 1) {
      setCurrentSlider(currentSlider + 1);
    } else {
      setCurrentSlider(0);
    }
  };

  const handlePrevSlider = () => {
    if (currentSlider > 0) {
      setCurrentSlider(currentSlider - 1);
    } else {
      setCurrentSlider(templatesToShow.length - 1);
    }
  };

  const checkedCheckbox = (
    <CheckCircle color="primary" style={{fontSize: '1.75rem'}} />
  );
  const uncheckedCheckbox = (
    <RadioButtonUnchecked style={{fontSize: '1.75rem'}} />
  );

  return (
    <div style={{position: 'relative'}}>
      {!hasCustomTemplateFeature && (
        <RestrictionModal featureLabel="Plantilla" />
      )}
      <Hidden xsDown>
        <Grid container spacing={1}>
          {templatesToShow.map((to, idx) => (
            <Grid key={to.id} item xs={12} sm={6} lg={4}>
              <div
                className={classes.templateItemContainer}
                style={{background: to.background}}
              >
                <CustomButton
                  className={classes.buttonSquare}
                  onClick={() => setTemplateType(`TEMPLATE_${idx + 1}`)}
                >
                  <div className={classes.skeletonSquare}>
                    <div className={classes.containerCheckedBox}>
                      {templateType === `TEMPLATE_${idx + 1}`
                        ? checkedCheckbox
                        : uncheckedCheckbox}
                    </div>
                    <img
                      className={classes.templateImage}
                      src={to.src}
                      alt={'template' + idx}
                    />
                  </div>
                </CustomButton>

                <CustomButton
                  href={to.storeUrl || ''}
                  target="_blank"
                  rel="noreferrer"
                  disabled={!to.storeUrl}
                  className={classes.goToStoreButton}
                >
                  Previsualizar
                </CustomButton>
              </div>
            </Grid>
          ))}
        </Grid>
      </Hidden>
      <Hidden smUp>
        <div className={classes.wrapSlider}>
          <div className={classes.slider}>
            <IconButton
              className={classes.arrowBack}
              color="primary"
              onClick={handlePrevSlider}
            >
              <ArrowBack />
            </IconButton>

            <div
              className={classes.itemSlider}
              style={{background: currentImage.background}}
            >
              <CustomButton
                className={classes.buttonSquare}
                onClick={() => setTemplateType(`TEMPLATE_${currentSlider + 1}`)}
              >
                <div className={classes.skeletonSquare}>
                  <div className={classes.containerCheckedBox}>
                    {templateType === `TEMPLATE_${currentSlider + 1}`
                      ? checkedCheckbox
                      : uncheckedCheckbox}
                  </div>
                  <img
                    className={classes.templateImage}
                    src={currentImage.src}
                    alt={'template' + currentSlider}
                  />
                </div>
              </CustomButton>
              {currentImage.storeUrl && (
                <CustomButton
                  href={currentImage.storeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={classes.goToStoreButton}
                >
                  Previsualizar
                </CustomButton>
              )}
            </div>

            <IconButton
              className={classes.arrowForward}
              color="primary"
              onClick={handleNextSlider}
            >
              <ArrowForward />
            </IconButton>
          </div>
        </div>

        <div className={classes.containerDots}>
          <div className={classes.dots}>
            {TEMPLATE_OPTIONS.map((to, idx) => (
              <CustomButton
                key={idx}
                className={classes.btnDot}
                onClick={() => handleViewSlider(idx)}
              >
                <div
                  className={`${classes.dot} ${
                    currentSlider === idx ? classes.dotSelected : ''
                  }`}
                />
              </CustomButton>
            ))}
          </div>
        </div>
      </Hidden>
    </div>
  );
}

TemplateSection.propTypes = {
  templateType: PropTypes.string,
  setTemplateType: PropTypes.func.isRequired,
};
